import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { HttpService } from './http.service';

@Injectable({
  providedIn: 'root',
})
export class ReportsService {
  constructor(private httpSvc: HttpService) {}

  getReportDownloadUrl(key: string) {
    return this.getFileDownloadUrl(key);
  }

  getFileDownloadUrl(key: string) {
    console.log('[ReportsService][S3 private flow] Requesting signed URL', { key });
    return this.httpSvc.get(`/common/files/download?key=${encodeURIComponent(key)}`).pipe(
      tap((response) => console.log('[ReportsService][S3 private flow] Signed URL response', response))
    );
  }

  resolveReportDownloadUrl(reportResponse: any): Observable<string> {
    console.log('[ReportsService][S3 private flow] Resolving report response', reportResponse);

    const key = this.getFileKey(reportResponse);

    if (key) {
      console.log('[ReportsService][S3 private flow] Report key detected, switching to signed URL', { key });
      return this.getFileDownloadUrl(key).pipe(
        switchMap((signedResponse) => {
          if (!signedResponse?.url) {
            console.error('[ReportsService][S3 private flow] Signed URL response without url', signedResponse);
            return throwError(new Error('Signed URL response without url'));
          }

          console.log('[ReportsService][S3 private flow] Signed URL ready', {
            key,
            expiresIn: signedResponse.expiresIn,
            signedUrlPrefix: signedResponse.url.substring(0, 120),
          });
          return of(signedResponse.url);
        })
      );
    }

    console.error('[ReportsService][S3 private flow] Could not find report key', reportResponse);
    return throwError(new Error('Could not find report key'));
  }

  resolveDocumentDownloadUrl(fileData: any): Observable<string> {
    console.log('[ReportsService][S3 private flow] Resolving document URL', { fileData });

    const directKey = this.getFileKey(fileData);
    if (directKey) {
      console.log('[ReportsService][S3 private flow] Document object key detected, switching to signed URL', {
        key: directKey,
      });
      return this.getFileDownloadUrl(directKey).pipe(
        switchMap((signedResponse) => {
          if (!signedResponse?.url) {
            console.error('[ReportsService][S3 private flow] Document signed URL response without url', signedResponse);
            return throwError(new Error('Document signed URL response without url'));
          }

          console.log('[ReportsService][S3 private flow] Document signed URL ready', {
            key: directKey,
            expiresIn: signedResponse.expiresIn,
            signedUrlPrefix: signedResponse.url.substring(0, 120),
          });
          return of(signedResponse.url);
        })
      );
    }

    const externalDocumentUrl = this.getAllowedExternalDocumentUrl(fileData);
    if (externalDocumentUrl) {
      console.log('[ReportsService][S3 private flow] Allowed external document URL detected', {
        externalUrlPrefix: externalDocumentUrl.substring(0, 120),
      });
      return of(externalDocumentUrl);
    }

    console.error('[ReportsService][S3 private flow] Document data has no key', { fileData });
    return throwError(new Error('Document data has no key'));
  }

  openDownload(fileData: any, filename: string, isReport = true): void {
    const resolver = isReport ? this.resolveReportDownloadUrl(fileData) : this.resolveDocumentDownloadUrl(fileData);
    resolver.subscribe({
      next: (downloadUrl) => {
        const element = document.createElement('a');
        element.setAttribute('href', downloadUrl);
        element.setAttribute('target', '_blank');
        element.setAttribute('download', filename);
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
      },
      error: (error) => {
        console.error('[ReportsService][S3 private flow] Error opening download', {
          fileData,
          filename,
          isReport,
          error,
        });
      },
    });
  }

  private getFileKey(fileData: any): string | undefined {
    if (!fileData) return undefined;
    if (typeof fileData === 'string' && !/^https?:\/\//.test(fileData)) return fileData;
    return fileData?.key || fileData?.url?.key || fileData?.file?.key || fileData?.reportFile?.key || fileData?.voucherFile?.key || fileData?.voucherKhipuFile?.key;
  }

  private getAllowedExternalDocumentUrl(fileData: any): string | undefined {
    const candidate = typeof fileData === 'string'
      ? fileData
      : typeof fileData?.url === 'string'
        ? fileData.url
        : undefined;

    if (!candidate || !/^https?:\/\//.test(candidate)) return undefined;

    try {
      const url = new URL(candidate);
      const host = url.hostname.toLowerCase();
      const isVessiUrl = host === 'vessi.cl' || host.endsWith('.vessi.cl');

      return isVessiUrl ? candidate : undefined;
    } catch (error) {
      console.error('[ReportsService][S3 private flow] Invalid external document URL', {
        candidate,
        error,
      });
      return undefined;
    }
  }

  getBranchReport(id: string, startDate?: string, endDate?: string) {
    let filtro = [];
    if (startDate) filtro.push(`startDate=${startDate} 00:00:00`);
    if (endDate) filtro.push(`endDate=${endDate} 23:59:59`);
    return this.httpSvc.get(`/report/branch/${id}?${filtro.join('&')}`);
  }

  getGlobalReport(id: string, startDate?: string, endDate?: string) {
    let filtro = [];
    if (startDate) filtro.push(`startDate=${startDate} 00:00:00`);
    if (endDate) filtro.push(`endDate=${endDate} 23:59:59`);
    return this.httpSvc.get(`/report/global/${id}?${filtro.join('&')}`);
  }

  getConsumerReport(companyId?: string, search?: string) {
    let filter = [];
    if (companyId) filter.push(`companyId=${companyId}`);
    if (search) filter.push(`search=${search}`);
    return this.httpSvc.get(`/report/all-consumers?${filter.join('&')}`);
  }

  getTransactionReport(
    companyId?: string,
    startDate?: string,
    endDate?: string,
    search?: string,
  ) {
    let filter = [];
    if (companyId) filter.push(`companyId=${companyId}`);
    if (startDate) filter.push(`startDate=${startDate} 00:00:00`);
    if (endDate) filter.push(`endDate=${endDate} 23:59:59`);
    if (search) filter.push(`search=${search}`);
    return this.httpSvc.get(`/report/all-transactions?${filter.join('&')}`);
  }

  //transaccion por colaborador
  getTransactionReportCollaborator(id?: string) {
    console.log('Id es:: ', id);
    return this.httpSvc.get(`/report/transactions-collaborators/${id}`);
  }
}
